/**
 * relationalMemoryEngine.js
 * Persistent per-entity pastoral memory across sessions.
 *
 * Stores lightweight signals (not conversations) for each entity seen.
 * Used to detect longitudinal patterns: trajectory changes, persistent risk,
 * recurring prayer requests, and ministry continuity after pastor transitions.
 *
 * Storage: localStorage — key per entity, 7-day TTL per signal, max 50 entries.
 */

const KEY_PREFIX  = "kevia_rel_v1_";
const SIGNAL_TTL  = 7 * 24 * 60 * 60 * 1000;   // 7 days per signal
const MAX_SIGNALS = 10;                           // per entity
const MAX_ENTRIES = 50;                           // total entities stored

/**
 * A pastoral signal snapshot saved each time an entity is viewed/discussed.
 * @typedef {{ ts: number, tone: string, visitDays: number|null, prayerCount: number, source: string }} PastoralSignal
 */

/**
 * An entity's full relational memory record.
 * @typedef {{ entityId: string, entityType: string, name: string, signals: PastoralSignal[], firstSeen: number, lastSeen: number }} RelationalRecord
 */

function _key(entityId) {
  return KEY_PREFIX + entityId;
}

function _allKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(KEY_PREFIX)) keys.push(k);
  }
  return keys;
}

/**
 * Load the relational record for an entity, or return null if unknown.
 * @param {string} entityId
 * @returns {RelationalRecord|null}
 */
export function getEntityHistory(entityId) {
  if (!entityId) return null;
  try {
    const raw = localStorage.getItem(_key(entityId));
    if (!raw) return null;
    const record = JSON.parse(raw);
    // Prune expired signals
    const now = Date.now();
    record.signals = (record.signals ?? []).filter((s) => now - s.ts < SIGNAL_TTL);
    return record.signals.length > 0 ? record : null;
  } catch {
    return null;
  }
}

/**
 * Record a new pastoral signal for an entity.
 * Called whenever Kevia processes a message with this entity in context.
 *
 * @param {string} entityId
 * @param {string} entityType   — "family" | "person" | "church"
 * @param {string} name
 * @param {PastoralSignal} signal
 */
export function recordEntitySignal(entityId, entityType, name, signal) {
  if (!entityId || !signal) return;
  try {
    _pruneOverflow();
    const existing = getEntityHistory(entityId);
    const prev     = existing ?? { entityId, entityType, name, signals: [], firstSeen: Date.now(), lastSeen: 0 };

    // Deduplicate: skip if last signal matches within 30 min (same session re-render)
    const last = prev.signals[0];
    if (last && (signal.ts - last.ts) < 30 * 60 * 1000 && last.tone === signal.tone) return;

    const updated = {
      ...prev,
      name,
      entityType,
      lastSeen: signal.ts,
      signals: [{ ...signal }, ...prev.signals].slice(0, MAX_SIGNALS),
    };

    localStorage.setItem(_key(entityId), JSON.stringify(updated));
  } catch {
    // localStorage quota — silent
  }
}

/**
 * Delete the relational memory for a specific entity.
 * @param {string} entityId
 */
export function clearEntityHistory(entityId) {
  if (!entityId) return;
  try { localStorage.removeItem(_key(entityId)); } catch { /* ignore */ }
}

/**
 * Returns all stored relational records, sorted by lastSeen descending.
 * @returns {RelationalRecord[]}
 */
export function getAllEntityHistories() {
  const keys    = _allKeys();
  const records = [];
  const now     = Date.now();

  for (const k of keys) {
    try {
      const raw    = localStorage.getItem(k);
      if (!raw) continue;
      const record = JSON.parse(raw);
      record.signals = (record.signals ?? []).filter((s) => now - s.ts < SIGNAL_TTL);
      if (record.signals.length > 0) records.push(record);
    } catch {
      // skip corrupt entry
    }
  }

  return records.sort((a, b) => b.lastSeen - a.lastSeen);
}

// ── Private ──────────────────────────────────────────────────────────────────

function _pruneOverflow() {
  const keys = _allKeys();
  if (keys.length < MAX_ENTRIES) return;

  // Remove oldest entries to stay under limit
  const withTs = keys.map((k) => {
    try {
      const r = JSON.parse(localStorage.getItem(k) ?? "{}");
      return { key: k, lastSeen: r.lastSeen ?? 0 };
    } catch {
      return { key: k, lastSeen: 0 };
    }
  });

  withTs.sort((a, b) => a.lastSeen - b.lastSeen);
  const toRemove = withTs.slice(0, withTs.length - MAX_ENTRIES + 1);
  for (const { key } of toRemove) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
}
